/**
 * investorDetail.ts — investor-first sections on the listing detail page.
 *
 * When state.user.user_type === 'investor', four sections are injected
 * into renderDetail() right after Details and before the trust block
 * (Photo-Truth, True Cost, HomeCheck, etc.):
 *
 *   1. Deal Analysis       — full breakdown with editable assumptions
 *   2. Financing Scenarios — DSCR / Conventional / Hard Money / Cash
 *   3. Rent Estimate       — estimated rent + confidence, override
 *
 * Legacy buyer flow is untouched — these render from a single branch in
 * listings.ts that no-ops for user_type !== 'investor'.
 */

import { api } from './api.js';
import { providerCtaSlot } from './providerCtas.js';
import { state } from './state.js';
import { escapeHtml, showToast } from './ui.js';
import { financingLabel, resolveEffectiveLoan } from '../../shared/financing.js';
import type { EffectiveLoanTerms, LoanTermOverrides } from '../../shared/financing.js';
import { formatMoneyCompact } from '../../shared/format.js';
import type { InvestmentAnalysisAssumptions, InvestorFinancingType, StrAssumptions } from '../../shared/types.js';
import { expenseRowHtml, marketStatHtml } from '../../shared/markup.js';
import { NA, countTile, expenseTile, moneyCompactTile, ratioPctTile, toNumber } from '../../shared/tiles.js';
import { capRateLevel, cashOnCashLevel, dealVerdictLabel, dealVerdictLevel, dscrLevel, financingScenariosFor, scenarioAssumptions, scenarioCacheKey } from '../../shared/underwriting.js';
import type { MetricLevel } from '../../shared/underwriting.js';
import {
  glossAfterTax,
  glossCapRate,
  glossCashFlow,
  glossCashOnCash,
  glossCashToClose,
  glossDscr,
  glossGrm,
} from '../../shared/glosses.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

/** The user's saved financing profile, resolved through the shared core.
 *  (shared/financing.ts is decoupled from app state — callers extract the
 *  profile fields and pass them in explicitly.) */
function userFinancingType(): InvestorFinancingType {
  const criteria = (state.user?.investment_criteria as Record<string, unknown> | undefined) || {};
  return (criteria.financing_type as InvestorFinancingType | undefined) || 'dscr';
}

/** The investor's fine-tuned down / rate / term from their profile —
 *  the overrides layered on top of their financing-type preset. */
function userLoanOverrides(): LoanTermOverrides {
  const criteria = (state.user?.investment_criteria as Record<string, unknown> | undefined) || {};
  return {
    downPct: criteria.down_payment_pct as number | undefined,
    ratePct: criteria.interest_rate as number | undefined,
    termYears: criteria.loan_term_years as number | undefined,
  };
}

function userLoanTerms(): EffectiveLoanTerms {
  return resolveEffectiveLoan(userFinancingType(), userLoanOverrides());
}

// ─── Section HTML (rendered inline by renderDetail) ─────────────────────

/** The "Your Deal" block — Deal Analysis + Financing Scenarios + Rent
 *  Comparables. Rendered right after Open House and before Due Diligence
 *  in the listing detail. Investor-first convergence: renders for every
 *  buyer-role user (cap rate / CoC / DSCR / cash flow are the headline
 *  numbers everyone wants before digging deeper). */
export function renderInvestorDealBlock(listing: AnyRecord): string {
  const id = escapeHtml(String(listing.id));
  return `
    <div class="detail-section inv-detail-section" id="inv-deal-analysis-section">
      <h3><i data-feather="trending-up" width="14" style="display:inline;vertical-align:-2px;margin-right:4px"></i> Deal Analysis</h3>
      <div id="inv-deal-analysis-content" data-listing-id="${id}">
        <div style="display:flex;align-items:center;gap:8px;padding:12px 0;color:var(--text-secondary)">
          <div class="spinner spinner-sm"></div>
          <span style="font-size:14px">Running numbers...</span>
        </div>
      </div>
    </div>

    ${providerCtaSlot('deal_analysis')}

    <div class="detail-section inv-detail-section" id="inv-financing-section">
      <h3><i data-feather="dollar-sign" width="14" style="display:inline;vertical-align:-2px;margin-right:4px"></i> Financing Scenarios</h3>
      <div id="inv-financing-content" data-listing-id="${id}">
        <div style="color:var(--text-secondary);font-size:13px">Loading scenarios...</div>
      </div>
    </div>

    ${providerCtaSlot('financing')}

    <div class="detail-section inv-detail-section" id="inv-rent-comps-section">
      <h3><i data-feather="home" width="14" style="display:inline;vertical-align:-2px;margin-right:4px"></i> Rent Estimate</h3>
      <div id="inv-rent-comps-content"></div>
    </div>

    <div class="detail-section inv-detail-section" id="inv-projection-section">
      <h3><i data-feather="trending-up" width="14" style="display:inline;vertical-align:-2px;margin-right:4px"></i> 5-Year & 10-Year Returns</h3>
      <div id="inv-projection-content">
        <div style="color:var(--text-secondary);font-size:13px">Projecting long-term returns...</div>
      </div>
    </div>

    <div class="detail-section inv-detail-section" id="inv-market-position-section">
      <h3><i data-feather="bar-chart-2" width="14" style="display:inline;vertical-align:-2px;margin-right:4px"></i> Market Position</h3>
      <div id="inv-market-position-content">
        <div style="color:var(--text-secondary);font-size:13px">Analyzing comps...</div>
      </div>
    </div>

    <div class="detail-section inv-detail-section inv-strategy-section" id="inv-offer-strategy-section">
      <h3><i data-feather="zap" width="14" style="display:inline;vertical-align:-2px;margin-right:4px"></i> Offer Strategy</h3>
      <div id="inv-offer-strategy-content">
        <div style="color:var(--text-secondary);font-size:13px">Building your strategy...</div>
      </div>
    </div>
  `;
}

// Back-compat alias — the earlier name. Kept so any inline onclicks or
// external callers that still reference it don't break.
export const renderInvestorDetailSections = renderInvestorDealBlock;

// ─── Assumptions — mutable in-flight scenario state ─────────────────────
// (Shape moved to shared/types.ts as InvestmentAnalysisAssumptions.)

const assumptionsByListing = new Map<string, InvestmentAnalysisAssumptions>();

/** Drop all per-listing slider state — used when the user's financing
 *  profile changes, so stale down-payment/rate terms never linger. */
export function clearInvestorAssumptions(): void {
  assumptionsByListing.clear();
}

function getAssumptions(id: string): InvestmentAnalysisAssumptions {  let a = assumptionsByListing.get(id);
  if (!a) {
    const criteria = (state.user?.investment_criteria as Record<string, unknown> | undefined) || {};
    // Slider defaults follow the user's saved financing profile: preset
    // for their financing type, overridden by their fine-tuned numbers.
    // This keeps the detail page consistent with the feed cards, which
    // the backend personalizes with the same profile.
    const terms = userLoanTerms();
    // Default rehab to the investor's saved max_rehab_budget if they set
    // one during onboarding. BRRRR/flip users onboard with $40-50K; buy-
    // and-hold onboards with 0. Zero means "turnkey, no rehab" and the
    // math collapses back to the previous cash_invested calculation.
    const defaultRehab = Number(criteria.max_rehab_budget || 0);
    a = {
      down_payment_pct: terms.downPct,
      interest_rate: terms.ratePct,
      vacancy_rate: 8,
      rehab_budget: defaultRehab,
      estimated_rent_override: null,
    };
    assumptionsByListing.set(id, a);
  }
  return a;
}

// ─── Loaders invoked from renderDetail's setTimeout ─────────────────────

/**
 * Fetch the full analysis and render all three data-backed sections
 * (deal analysis, rent comps, financing). The providers section is
 * static so it doesn't need a loader.
 */
// Monotonic per-listing request counter backing the sequence guard.
const investorDetailSeq = new Map<string, number>();

export async function loadInvestorDetail(listingId: string): Promise<void> {
  // Sequence guard: rapid recalcs (slider release, Recalculate taps)
  // resolve out of order — only the latest response may paint, so a
  // slow earlier request can't overwrite newer numbers.
  const seq = (investorDetailSeq.get(listingId) || 0) + 1;
  investorDetailSeq.set(listingId, seq);
  try {
    const data = await api<AnyRecord>(
      'POST',
      `/listings/${encodeURIComponent(listingId)}/investment-analysis/custom`,
      getAssumptions(listingId),
    );
    if (investorDetailSeq.get(listingId) !== seq) return;
    renderDealAnalysis(listingId, data);
    renderRentComps(listingId, data);
    renderFinancingScenarios(listingId);
    renderProjection(data);
    // Market Position folds in Offer Insight — fired parallel to the
    // analysis so it doesn't block the main deal render.
    loadMarketPosition(listingId);
    // Strategy-aware offer guidance — deterministic, no LLM; depends on
    // the investor's saved criteria (strategy, target cap, rehab budget)
    // and whatever comps the offer-insight cache has.
    loadOfferStrategy(listingId);
  } catch (err: any) {
    // No score yet → drop the strip rather than leave a spinner up top.
    const strip = document.getElementById('detail-score-strip');
    if (strip?.classList.contains('score-strip-loading')) strip.remove();
    const el = document.getElementById('inv-deal-analysis-content');
    if (!el) return;
    // A failed RECALCULATION must not nuke the working UI (sliders +
    // last-good numbers) — that strands the user with no retry path.
    // Only show the error state when nothing has rendered yet.
    if (el.querySelector('.inv-hero-tile')) {
      showToast('Could not recalculate — check your connection', 'error');
      return;
    }
    el.innerHTML = `<div style="color:var(--text-secondary);font-size:13px">${escapeHtml(err.message || 'Failed to load analysis')}</div>`;
  }
}

// ─── Long-term projection (5yr / 10yr IRR) ──────────────────────────────
//
// Cash flow alone understates a buy-and-hold's return by 30-50% because
// it ignores appreciation, principal paydown, and tax benefits. This
// section shows side-by-side 5yr and 10yr total return + IRR. Formulas
// live in services/projection.py on the backend.

function renderProjection(data: AnyRecord): void {
  const el = document.getElementById('inv-projection-content');
  if (!el) return;
  const projection = (data.projection as AnyRecord) || {};
  const summaries = (projection.summaries as AnyRecord) || {};
  const a = (projection.assumptions_used as AnyRecord) || {};

  const renderCol = (key: string, label: string): string => {
    const s = summaries[key] as AnyRecord | undefined;
    if (!s) return '';
    const irr = s.irr != null ? `${Number(s.irr).toFixed(1)}%` : '—';
    const irrColor = s.irr == null
      ? 'var(--text-muted)'
      : Number(s.irr) >= 12
        ? 'var(--success)'
        : Number(s.irr) >= 6
          ? 'var(--warning)'
          : 'var(--danger)';
    const totalReturn = Number(s.total_return || 0);
    const cumCf = Number(s.cumulative_cash_flow || 0);
    const equity = Number(s.projected_equity || 0);
    const taxBenefits = Number(s.accumulated_tax_benefits || 0);
    const moic = Number(s.moic || 0);

    return `
      <div class="inv-proj-col">
        <div class="inv-proj-col-head">${escapeHtml(label)}</div>
        <div class="inv-proj-irr" style="color:${irrColor}">${irr}<span class="inv-proj-irr-label">IRR</span></div>
        <div class="inv-proj-rows">
          <div class="inv-proj-row"><span>Total return</span><span class="${totalReturn >= 0 ? 'pos' : 'neg'}"><strong>${formatMoneyCompact(totalReturn)}</strong></span></div>
          <div class="inv-proj-row"><span>MOIC</span><span><strong>${moic.toFixed(2)}x</strong></span></div>
          <div class="inv-proj-row"><span>Projected equity</span><span>${formatMoneyCompact(equity)}</span></div>
          <div class="inv-proj-row"><span>Cumulative cash flow</span><span class="${cumCf >= 0 ? 'pos' : 'neg'}">${formatMoneyCompact(cumCf)}</span></div>
          ${taxBenefits > 0 ? `<div class="inv-proj-row"><span>Tax benefits</span><span class="pos">+${formatMoneyCompact(taxBenefits)}</span></div>` : ''}
        </div>
      </div>
    `;
  };

  const cols = ['5yr', '10yr'].map((k) => renderCol(k, k.replace('yr', '-year'))).join('');
  if (!cols) {
    el.innerHTML = `<div style="color:var(--text-secondary);font-size:13px">Projection not available for this listing yet.</div>`;
    return;
  }

  const apr = Number(a.appreciation_pct || 3.5);
  const rentG = Number(a.rent_growth_pct || 3.0);

  el.innerHTML = `
    <div class="inv-proj-assumptions">
      Assumes ${apr.toFixed(1)}% appreciation, ${rentG.toFixed(1)}% rent growth, ${Number(a.selling_costs_pct || 7).toFixed(0)}% selling costs at exit.
    </div>
    <div class="inv-proj-grid">${cols}</div>
  `;
}

// ─── Market Position (folded-in Offer Insight) ──────────────────────────
//
// Investors absolutely care about sale-to-list ratio + days-on-market +
// comp median — it's what they use to negotiate the cap rate up. The
// buyer-framed "Offer Strength" rating is repurposed here as a
// "negotiation leverage" signal rather than a "should I offer this?"
// question.

async function loadMarketPosition(listingId: string): Promise<void> {
  const el = document.getElementById('inv-market-position-content');
  if (!el) return;
  try {
    const data = await api<AnyRecord>(
      'GET',
      `/listings/${encodeURIComponent(listingId)}/market-position`,
    );
    renderMarketPosition(el, data);
  } catch (_err) {
    el.innerHTML = `
      <div style="color:var(--text-secondary);font-size:13px;padding:4px 0">
        Comp data not available for this listing yet.
      </div>
    `;
  }
}

function renderMarketPosition(el: HTMLElement, data: AnyRecord): void {
  const market = (data.market_data as AnyRecord) || {};
  const strength = (data.offer_strength as AnyRecord) || {};
  const l = (data.listing as AnyRecord) || {};

  // listPrice was used by the dropped suggested-offer band — removed.
  // comp_cache_market_data sends the ratio under both keys; read either.
  const saleToList = market.avg_sale_to_list_ratio ?? market.sale_to_list_ratio;
  const ratio = Number(saleToList || 0);
  // Median $/sqft is a small whole-dollar figure; months of supply is a
  // one-decimal count — both render "n/a" when the cache has no figure.
  const psf = toNumber(market.median_price_per_sqft);
  const psfTile = psf == null || psf <= 0 ? NA : `$${Math.round(psf).toLocaleString('en-US')}`;
  const mos = toNumber(market.months_of_supply);
  const mosTile = mos == null || mos < 0 ? NA : mos.toFixed(1);

  // Ratio interpretation:
  //   < 0.95  buyers getting deals — high negotiation leverage
  //   0.95-1.00  balanced — room to negotiate a bit
  //   > 1.00  seller's market — offering over list is common
  let leverage = 'Neutral market';
  let leverageClass = 'inv-verdict-yellow';
  if (ratio > 0 && ratio < 0.95) {
    leverage = 'Strong buyer leverage';
    leverageClass = 'inv-verdict-green';
  } else if (ratio > 1.0) {
    leverage = "Seller's market";
    leverageClass = 'inv-verdict-red';
  }

  // QA pass 2 / redundancy #1: Market Position previously rendered a
  // "Suggested offer range" anchored at sale-to-list ratio. Offer Strategy
  // (rendered just above) already gives a strategy-specific offer band
  // anchored to the user's cap-rate / BRRRR / Flip target, so the two
  // numbers conflict — comp-anchored ~$1.13M vs cap-rate-anchored ~$697K
  // for the same listing. Dropped the Market Position version; Offer
  // Strategy owns "what should I offer" guidance. The strength label
  // ("At list price your offer reads as Competitive") stays because it
  // evaluates the LIST price specifically, not a suggested offer.
  const strengthLabel = strength.label ? String(strength.label) : null;

  el.innerHTML = `
    <div class="inv-market-leverage ${leverageClass}">
      <div class="inv-market-leverage-label">Negotiation leverage</div>
      <div class="inv-market-leverage-value">${escapeHtml(leverage)}</div>
    </div>

    <details class="inv-deal-breakdown inv-collapse">
      <summary>Market details</summary>
    <div class="inv-market-grid">
      <!-- shared/tiles.ts + markup.ts: every value is text ("n/a" when the
           comps cache has no figure) and every tile names itself
           "Label: value" — never a bare dash or an empty tile. -->
      ${marketStatHtml('Sale-to-list', ratioPctTile(saleToList), escapeHtml)}
      ${marketStatHtml('Avg DOM', countTile(market.avg_days_on_market), escapeHtml)}
      ${marketStatHtml('This listing', countTile(l.days_on_market), escapeHtml)}
      ${marketStatHtml('Median comp', moneyCompactTile(market.median_sale_price), escapeHtml)}
      ${marketStatHtml('Median $/sqft', psfTile, escapeHtml)}
      ${marketStatHtml('Months of supply', mosTile, escapeHtml)}
      ${marketStatHtml('Active inventory', countTile(market.active_inventory_count ?? market.active_inventory, { zeroIsValue: true }), escapeHtml)}
    </div>

    ${strengthLabel ? `
      <div class="inv-market-strength">
        <i data-feather="target" width="12"></i>
        <span>At list price, your offer reads as <strong>${escapeHtml(strengthLabel)}</strong>.</span>
      </div>
    ` : ''}
    </details>
  `;

  if (window.feather) window.feather.replace();
}

// ─── Deal Analysis ──────────────────────────────────────────────────────

function fmtMoney(n: number): string {
  const sign = n >= 0 ? '' : '−';
  return `${sign}$${Math.abs(Math.round(n)).toLocaleString()}`;
}

function fmtPct(n: number): string {
  return `${Number(n).toFixed(2)}%`;
}

/** Compact verdict strip under the stats row (#detail-score-strip in
 *  renderDetail). Painted from the SAME analysis payload as Deal Analysis
 *  on every render/recalc, so the two can never disagree. */
function renderScoreStrip(data: AnyRecord): void {
  const el = document.getElementById('detail-score-strip');
  if (!el) return;
  const score = Number(data.investment_score ?? 0);
  const verdictKey = String(data.deal_verdict || '');
  const level = dealVerdictLevel(verdictKey);
  const cap = data.cap_rate == null ? null : Number(data.cap_rate);
  const dscr = data.dscr_ratio == null ? null : Number(data.dscr_ratio);
  const cashFlow = Number(data.cash_flow?.monthly_pre_tax ?? 0);
  // shared/tiles NA ("n/a") — the same text the swipe card, Watchlist and
  // Compare grid show for an uncomputable ratio (cash purchase → no DSCR).
  const dash = `<span class="score-strip-na">${NA}</span>`;
  el.classList.remove('score-strip-loading');
  el.innerHTML = `
    <div class="score-strip-score">
      <span class="score-strip-num inv-score-${level}">${score}</span>
      <span class="score-strip-verdict inv-verdict-${level}">${escapeHtml(dealVerdictLabel(verdictKey))}</span>
    </div>
    <div class="score-strip-metrics">
      <div class="score-strip-metric"><span class="score-strip-val" style="color:${capColor(cap)}">${cap == null ? dash : fmtPct(cap)}</span><span class="score-strip-lbl">Cap rate</span></div>
      <div class="score-strip-metric"><span class="score-strip-val ${cashFlow >= 0 ? 'pos' : 'neg'}">${fmtMoneyShort(cashFlow)}</span><span class="score-strip-lbl">Cash flow/mo</span></div>
      <div class="score-strip-metric"><span class="score-strip-val" style="color:${dscrColor(dscr)}">${dscr == null ? dash : dscr.toFixed(2)}</span><span class="score-strip-lbl">DSCR</span></div>
    </div>
  `;
}

// Shared metric thresholds (shared/underwriting.ts) → CSS colors, so the
// strip, the hero tiles and the mobile app all color a value the same way.
function metricColor(level: MetricLevel): string {
  return level === 'green' ? 'var(--success)' : level === 'yellow' ? 'var(--warning)' : level === 'red' ? 'var(--danger)' : 'var(--text-muted)';
}
function capColor(v: number | null): string {
  return metricColor(capRateLevel(v));
}
function dscrColor(v: number | null): string {
  return metricColor(dscrLevel(v));
}

function fmtMoneyShort(n: number): string {
  const sign = n < 0 ? '\u2212' : '';
  const abs = Math.abs(n);
  return abs >= 10000 ? `${sign}$${(abs / 1000).toFixed(1)}K` : `${sign}$${Math.round(abs).toLocaleString()}`;
}

function renderDealAnalysis(listingId: string, data: AnyRecord): void {
  const el = document.getElementById('inv-deal-analysis-content');
  if (!el) return;

  const a = getAssumptions(listingId);
  const score = Number(data.investment_score ?? 0);
  const verdictKey = String(data.deal_verdict || '');
  const verdict = { label: dealVerdictLabel(verdictKey), cls: `inv-verdict-${dealVerdictLevel(verdictKey)}` };
  // Ratios can be null when uncomputable (cash purchase → no DSCR,
  // zero price → no cap rate). Null renders as "n/a", never as 0.00 —
  // a zero would read as a measured value.
  const cap = data.cap_rate == null ? null : Number(data.cap_rate);
  const coc = data.cash_on_cash_return == null ? null : Number(data.cash_on_cash_return);
  const dscr = data.dscr_ratio == null ? null : Number(data.dscr_ratio);
  const grm = data.gross_rent_multiplier == null ? null : Number(data.gross_rent_multiplier);
  const rent = Number(data.estimated_monthly_rent ?? 0);
  const cashFlow = Number(data.cash_flow?.monthly_pre_tax ?? 0);
  const noi = Number(data.noi ?? 0);
  const opex = (data.operating_expenses as AnyRecord) || {};
  const mort = (data.mortgage as AnyRecord) || {};

  // Wishlist #9: "Why this score?" expander. Reads the new score_breakdown
  // array from the analyzer and renders each component with its raw value,
  // contribution out of weight cap, and a one-line threshold explanation.
  // Surfaces the user's own floors (min_cap_rate / min_cash_on_cash /
  // min_dscr from investment_criteria) so the explainer can call out
  // which components clear or miss the user's bar.
  const breakdown = (data.score_breakdown as AnyRecord[]) || [];
  const criteria = (state.user?.investment_criteria as AnyRecord | undefined) || {};
  const userFloors: Record<string, number | undefined> = {
    'Cap rate': criteria.min_cap_rate as number | undefined,
    'Cash-on-cash': criteria.min_cash_on_cash as number | undefined,
    DSCR: criteria.min_dscr as number | undefined,
  };
  const explainerRows = breakdown
    .map((row: AnyRecord) => {
      const label = String(row.label || '');
      const valueLabel = String(row.value_label || '');
      const points = Number(row.points || 0);
      const maxPoints = Number(row.max_points || 0);
      const threshold = String(row.threshold || '');
      const pct = maxPoints > 0 ? Math.round((points / maxPoints) * 100) : 0;
      const barColor = pct >= 70 ? 'var(--success)' : pct >= 40 ? 'var(--warning)' : 'var(--danger)';

      // Compare against the user's own floor if one is set for this metric.
      let userNote = '';
      const floor = userFloors[label];
      // A null metric (cash purchase → no DSCR) has no floor verdict —
      // coercing it to 0 printed "Below your 1.25 floor" for a deal with
      // no debt at all.
      if (floor != null && row.value != null) {
        const raw = Number(row.value);
        if (raw < floor) {
          userNote = `<span style="color:var(--danger);font-weight:600">Below your ${floor}${label === 'DSCR' ? '' : '%'} floor</span>`;
        } else {
          userNote = `<span style="color:var(--success);font-weight:600">Clears your ${floor}${label === 'DSCR' ? '' : '%'} floor</span>`;
        }
      }

      return `
        <div style="padding:10px 0;border-top:1px solid var(--border)">
          <div style="display:flex;justify-content:space-between;align-items:baseline;gap:12px;margin-bottom:4px">
            <div style="font-size:13px;font-weight:600;color:var(--text-primary)">${escapeHtml(label)}</div>
            <div style="font-size:13px;font-weight:700;color:var(--text-primary)">${escapeHtml(valueLabel)}</div>
          </div>
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
            <div style="flex:1;height:6px;background:var(--surface);border-radius:3px;overflow:hidden">
              <div style="width:${pct}%;height:100%;background:${barColor};border-radius:3px"></div>
            </div>
            <div style="font-size:11px;font-weight:600;color:var(--text-secondary);min-width:64px;text-align:right">${points.toFixed(1)} / ${maxPoints.toFixed(0)} pts</div>
          </div>
          <div style="font-size:11px;color:var(--text-muted)">${escapeHtml(threshold)}${userNote ? ' · ' + userNote : ''}</div>
        </div>
      `;
    })
    .join('');

  renderScoreStrip(data);

  el.innerHTML = `
    <div class="inv-deal-legend"><i data-feather="info" width="12"></i> All figures are estimates based on available data.</div>
    <div class="inv-deal-hero">
      <div class="inv-deal-score">
        <div class="inv-deal-score-value">${score}</div>
        <div class="inv-deal-score-label">Investment score</div>
      </div>
      <div class="inv-deal-verdict ${verdict.cls}">${verdict.label}</div>
    </div>
    ${(() => {
      // Financing provenance: whose loan these numbers assume. Prefers
      // the backend's label (it knows about interest-only bridges);
      // falls back to the client-side label from the saved profile.
      const finLabel = String(data.financing_label || '') || financingLabel(userFinancingType(), userLoanTerms().downPct);
      const au = (data.assumptions_used as AnyRecord) || {};
      const bits: string[] = [];
      if (au.down_payment_pct != null) bits.push(`${Number(au.down_payment_pct).toFixed(0)}% down`);
      if (au.interest_rate != null) bits.push(`${Number(au.interest_rate).toFixed(2)}%`);
      if (au.loan_term_years != null && au.interest_only !== true) bits.push(`${au.loan_term_years}-yr`);
      if (au.interest_only === true) bits.push('interest-only');
      const detail = bits.length ? ` · ${bits.join(' · ')}` : '';
      return `<div class="inv-fin-line" title="Change your loan terms in Profile → Investment criteria">Based on ${escapeHtml(finLabel)}${escapeHtml(detail)}</div>`;
    })()}

    ${(() => {
      // Tiered tiles: three hero metrics at full emphasis next to the
      // score, the rest in a quieter secondary grid. Every tile carries a
      // plain-English caption derived from its actual value (shared/glosses).
      const shield = (data.tax_shield as AnyRecord) || {};
      const annualPre = Number(data.cash_flow?.annual_pre_tax || 0);
      const annualAfter = Number(data.cash_flow?.annual_after_tax ?? annualPre);
      const cashToClose = Number(data.cash_flow?.total_cash_invested || 0);
      const price = Number(state.selectedListing?.price ?? 0);
      const na = `<span class="inv-tile-na">${NA}</span>`;
      const hero = (label: string, value: string, color: string, gloss: string, title = '') => `
        <div class="inv-hero-tile"${title ? ` title="${escapeHtml(title)}"` : ''}>
          <div class="inv-hero-label">${label}</div>
          <div class="inv-hero-value" style="color:${color}">${value}</div>
          <div class="inv-gloss">${escapeHtml(gloss)}</div>
        </div>`;
      const minor = (label: string, value: string, color: string, gloss: string) => `
        <div class="inv-minor-tile">
          <div class="inv-minor-top"><span class="inv-minor-label">${label}</span><span class="inv-minor-value" style="color:${color}">${value}</span></div>
          <div class="inv-gloss">${escapeHtml(gloss)}</div>
        </div>`;
      return `
        <div class="inv-hero-metrics">
          ${hero('Monthly cash flow', `${fmtMoney(cashFlow)}<span class="inv-hero-unit">/mo</span>`, cashFlow >= 0 ? 'var(--success)' : 'var(--danger)', glossCashFlow(cashFlow, rent))}
          ${hero('Cap rate', cap == null ? na : fmtPct(cap), metricColor(capRateLevel(cap)), glossCapRate(cap))}
          ${hero('Cash on cash', coc == null ? na : fmtPct(coc), metricColor(cashOnCashLevel(coc)), glossCashOnCash(coc), 'Annual pre-tax cash flow ÷ total cash invested (down payment + closing costs + rehab)')}
        </div>
        <div class="inv-minor-metrics">
          ${minor('DSCR', dscr == null ? na : dscr.toFixed(2), metricColor(dscrLevel(dscr)), glossDscr(dscr))}
          ${minor('GRM', grm == null ? na : grm.toFixed(2), 'var(--text)', glossGrm(grm))}
          ${minor('Cash to close', fmtMoney(cashToClose), 'var(--text)', glossCashToClose(cashToClose, price))}
          ${minor('After-tax cash flow', `${fmtMoney(annualAfter)}/yr`, annualAfter >= 0 ? 'var(--success)' : 'var(--danger)', glossAfterTax(annualAfter, annualPre, shield.tax_benefit == null ? null : Number(shield.tax_benefit)))}
        </div>
      `;
    })()}

    ${breakdown.length ? `
      <details class="inv-deal-explainer" style="margin:8px 0 14px;border:1px solid var(--border);border-radius:8px;padding:0 12px;background:rgba(255,255,255,0.02)">
        <summary style="padding:10px 0;cursor:pointer;font-size:13px;font-weight:600;color:var(--primary);user-select:none">Why this score?</summary>
        <div style="padding-bottom:8px">
          ${explainerRows}
          <div style="margin-top:10px;padding-top:10px;border-top:1px solid var(--border);font-size:11px;color:var(--text-muted);line-height:1.5">
            Score is a weighted composite of cap rate (25%), cash-on-cash (25%), DSCR (20%), price-to-rent (15%), days on market (10%), and price-reduction (5%). Higher is better; 50 is the recommend-vs-pass threshold the For You feed uses by default.
          </div>
        </div>
      </details>
    ` : ''}

    <details class="inv-deal-breakdown">
      <summary>Income + expense breakdown</summary>
      <div class="inv-deal-breakdown-rows">
        <div class="inv-deal-row"><span>Gross annual rent</span><span>${fmtMoney(Number(data.gross_annual_rent || 0))}</span></div>
        <div class="inv-deal-row"><span>Vacancy (${a.vacancy_rate}%)</span><span class="neg">−${fmtMoney(Number(data.vacancy_loss || 0))}</span></div>
        <div class="inv-deal-row"><span>Effective gross income</span><span>${fmtMoney(Number(data.effective_gross_income || 0))}</span></div>
        ${expenseRowHtml('Property tax', expenseTile(opex.property_tax), escapeHtml, { tone: 'neg', src: opex.property_tax_source ? String(opex.property_tax_source) : null })}
        ${expenseRowHtml('Insurance', expenseTile(opex.insurance), escapeHtml, { tone: 'neg', src: opex.insurance_source ? String(opex.insurance_source) : null })}
        ${expenseRowHtml('Property mgmt', expenseTile(opex.property_management), escapeHtml, { tone: 'neg', src: Number(opex.property_management) === 0 ? 'self-managed' : null })}
        ${expenseRowHtml('Maintenance reserve', expenseTile(opex.maintenance_reserve), escapeHtml, { tone: 'neg' })}
        ${(() => {
          const hoaAmt = Number(opex.hoa || 0);
          if (hoaAmt > 0) return `<div class="inv-deal-row inv-deal-row-sub"><span>HOA</span><span class="neg">−${fmtMoney(hoaAmt)}</span></div>`;
          // Tri-state honesty: a confirmed no-HOA renders $0-equivalent
          // "None"; an unknown HOA renders "unknown" — never a silent $0.
          if (opex.hoa_known) return `<div class="inv-deal-row inv-deal-row-sub"><span>HOA</span><span>None</span></div>`;
          return `<div class="inv-deal-row inv-deal-row-sub"><span>HOA</span><span><em class="inv-deal-row-src">unknown</em></span></div>`;
        })()}
        <div class="inv-deal-row"><span>NOI</span><span><strong>${fmtMoney(noi)}</strong></span></div>
        <div class="inv-deal-row"><span>Annual debt service</span><span class="neg">−${fmtMoney(Number(mort.annual_debt_service || 0))}</span></div>
        <div class="inv-deal-row inv-deal-row-total"><span>Annual pre-tax cash flow</span><span class="${cashFlow >= 0 ? 'pos' : 'neg'}"><strong>${fmtMoney(Number(data.cash_flow?.annual_pre_tax || 0))}</strong></span></div>
        ${(() => {
          const shield = (data.tax_shield as AnyRecord) || {};
          const bracket = Number(shield.tax_bracket_pct || 0);
          if (!bracket) return '';
          const afterTax = Number(data.cash_flow?.annual_after_tax || 0);
          const benefit = Number(shield.tax_benefit || 0);
          return `
            <div class="inv-deal-row inv-deal-row-sub" style="padding-top:6px;margin-top:4px;border-top:1px dashed rgba(255,255,255,0.05)">
              <span>Depreciation (27.5yr) <em class="inv-deal-row-src">deduction</em></span><span>−${fmtMoney(Number(shield.annual_depreciation || 0))}</span>
            </div>
            <div class="inv-deal-row inv-deal-row-sub">
              <span>Interest <em class="inv-deal-row-src">deduction</em></span><span>−${fmtMoney(Number(shield.annual_interest_deduction || 0))}</span>
            </div>
            <div class="inv-deal-row inv-deal-row-sub">
              <span>Tax benefit @ ${bracket}% bracket</span><span class="${benefit >= 0 ? 'pos' : 'neg'}">${benefit >= 0 ? '+' : ''}${fmtMoney(benefit)}</span>
            </div>
            <div class="inv-deal-row inv-deal-row-total">
              <span>Annual after-tax cash flow</span>
              <span class="${afterTax >= 0 ? 'pos' : 'neg'}"><strong>${fmtMoney(afterTax)}</strong></span>
            </div>
            <div style="font-size:11px;color:var(--text-muted);line-height:1.5;margin-top:6px">
              Depreciation and interest are deductions used to compute the tax benefit — not extra cash on top of it.
              Deductibility and passive-loss use depend on your tax situation; this is an estimate, not tax advice.
            </div>
          `;
        })()}
      </div>
    </details>

    <div class="inv-deal-assumptions">
      <div class="inv-deal-assumptions-head">Your assumptions</div>
      ${(() => {
        // Cash buyers have no loan: sliders for down payment / rate are
        // meaningless, so lock the row instead of showing a clamped slider.
        const isCash = userFinancingType() === 'cash';
        if (isCash) {
          return `<div class="inv-deal-assumption"><label>Financing</label>
            <div class="inv-cash-locked">Cash purchase — no loan, no DSCR</div></div>`;
        }
        return `
      <div class="inv-deal-assumption">
        <label>Down payment <span id="inv-dp-val">${a.down_payment_pct}%</span></label>
        <input type="range" min="5" max="50" step="5" value="${a.down_payment_pct}"
               oninput="onInvDownPayment(this.value, '${listingId}')"
               onchange="recalcInvestorDeal('${listingId}')">
      </div>
      <div class="inv-deal-assumption">
        <label>Interest rate <span id="inv-ir-val">${a.interest_rate.toFixed(2)}%</span></label>
        <input type="range" min="4" max="12" step="0.25" value="${a.interest_rate}"
               oninput="onInvInterestRate(this.value, '${listingId}')"
               onchange="recalcInvestorDeal('${listingId}')">
      </div>`;
      })()}
      <div class="inv-deal-assumption">
        <label>Vacancy <span id="inv-vac-val">${a.vacancy_rate}%</span></label>
        <input type="range" min="0" max="20" step="1" value="${a.vacancy_rate}"
               oninput="onInvVacancy(this.value, '${listingId}')"
               onchange="recalcInvestorDeal('${listingId}')">
      </div>
      <div class="inv-deal-assumption">
        <label>Rehab budget <span id="inv-rehab-val">${a.rehab_budget > 0 ? '$' + a.rehab_budget.toLocaleString() : 'Turnkey'}</span></label>
        <input type="range" min="0" max="150000" step="2500" value="${a.rehab_budget}"
               oninput="onInvRehab(this.value, '${listingId}')"
               onchange="recalcInvestorDeal('${listingId}')">
      </div>
      <div class="inv-deal-actions-row">
        <button class="btn btn-primary" onclick="recalcInvestorDeal('${listingId}')" style="flex:2">Recalculate</button>
        <button class="btn btn-secondary" onclick="saveInvestorDeal('${listingId}')" style="flex:1">
          <i data-feather="bookmark" width="14"></i>
          <span>Save</span>
        </button>
      </div>
    </div>
  `;

  if (window.feather) window.feather.replace();
  // Color the primary metrics in-line after the DOM is updated.
}

// ─── Assumption sliders + recalc ────────────────────────────────────────

export function onInvDownPayment(v: string, id: string): void {
  const a = getAssumptions(id);
  a.down_payment_pct = Number(v);
  const lbl = document.getElementById('inv-dp-val');
  if (lbl) lbl.textContent = `${a.down_payment_pct}%`;
}

export function onInvInterestRate(v: string, id: string): void {
  const a = getAssumptions(id);
  a.interest_rate = Number(v);
  const lbl = document.getElementById('inv-ir-val');
  if (lbl) lbl.textContent = `${a.interest_rate.toFixed(2)}%`;
}

export function onInvVacancy(v: string, id: string): void {
  const a = getAssumptions(id);
  a.vacancy_rate = Number(v);
  const lbl = document.getElementById('inv-vac-val');
  if (lbl) lbl.textContent = `${a.vacancy_rate}%`;
}

export function onInvRehab(v: string, id: string): void {
  const a = getAssumptions(id);
  a.rehab_budget = Number(v);
  const lbl = document.getElementById('inv-rehab-val');
  if (lbl) lbl.textContent = a.rehab_budget > 0 ? '$' + a.rehab_budget.toLocaleString() : 'Turnkey';
}

export async function recalcInvestorDeal(id: string): Promise<void> {
  await loadInvestorDetail(id);
}

/** Save the current assumptions + metrics to deal_analyses so the
 *  investor can revisit this deal from the Portfolio tab's Saved Deals
 *  list. Posts through the same /save-deal endpoint so the assumption
 *  bundle lives server-side — sessionStorage isn't enough when comparing
 *  across devices. */
export async function saveInvestorDeal(id: string): Promise<void> {
  try {
    await api(
      'POST',
      `/listings/${encodeURIComponent(id)}/save-deal`,
      getAssumptions(id),
    );
    // Saved deals live on the Watchlist (Portfolio is owned property only).
    showToast('Deal saved to your Watchlist.', 'success');
  } catch (err: any) {
    showToast(err.message || 'Could not save deal', 'error');
  }
}

// ─── Financing Scenarios ────────────────────────────────────────────────
// (Scenario table lives in shared/underwriting.ts — financingScenariosFor
// returns the four presets with the investor's OWN type row carrying their
// resolved terms, so the "Your basis" row equals the hero tiles.)

// See renderFinancingScenarios — per detail-view scenario cache.
let scenarioCache: { el: HTMLElement; key: string } | null = null;

async function renderFinancingScenarios(listingId: string): Promise<void> {
  const container = document.getElementById('inv-financing-content');
  if (!container) return;

  // Each scenario is the user's CURRENT slider assumptions (vacancy,
  // rehab, rent override) with only the financing swapped for the
  // scenario's down / rate / term / loan structure — the same recalc
  // path as the hero metrics, so the rows can't go stale after a slider
  // edit. The row for the investor's own financing type carries their
  // resolved (fine-tuned) terms, so "Your basis" reproduces the hero
  // tiles exactly. The cache key carries the non-financing assumptions
  // + the user's financing profile, so a slider or profile change
  // invalidates it while a plain re-render still skips the four requests.
  const assumptions = getAssumptions(listingId);
  const activeKey = userFinancingType();
  const scenarios = financingScenariosFor(activeKey, userLoanOverrides());
  const key = `${scenarioCacheKey(listingId, assumptions)}|${activeKey}|${JSON.stringify(userLoanTerms())}`;
  if (scenarioCache && scenarioCache.el === container && scenarioCache.key === key) return;

  // Fetch one analysis per scenario so the numbers are exact. Four parallel
  // requests; cheap because the analyzer is stateless + CPU-bound in the app.
  const results = await Promise.all(
    scenarios.map((s) =>
      api<AnyRecord>(
        'POST',
        `/listings/${encodeURIComponent(listingId)}/investment-analysis/custom`,
        scenarioAssumptions(assumptions, s),
      ).catch(() => null),
    ),
  );
  // Cache only a clean sweep — a partial failure retries on next recalc.
  if (results.every(Boolean)) scenarioCache = { el: container, key };

  // The row matching the investor's saved financing basis (the "Based on
  // … loan" line in Deal Analysis) is emphasized; the others are dimmed.
  container.innerHTML = `
    <div class="inv-fin-table">
      <div class="inv-fin-row inv-fin-head">
        <div>Scenario</div>
        <div>Down</div>
        <div>Monthly P&amp;I</div>
        <div>Cash flow</div>
        <div>DSCR</div>
      </div>
      ${scenarios.map((s, i) => {
        const r = results[i];
        // Loan terms the row was computed with — "25% down · 7.50% ·
        // 30-yr", or "12-mo IO" for the interest-only bridge. Cash has
        // no loan, so the blurb stands alone.
        const terms = s.key === 'cash'
          ? ''
          : ` · ${s.down_pct}% down · ${s.rate.toFixed(2)}% · ${s.interest_only ? '12-mo IO' : `${s.term_years}-yr`}`;
        const rowCls = s.key === activeKey ? 'inv-fin-row inv-fin-active' : 'inv-fin-row inv-fin-dim';
        // The shared preset label for the DSCR product is just "DSCR",
        // which next to a DSCR column read as the viewer's own ratio —
        // name the loan product here instead (display only; shared
        // FINANCING_SCENARIOS keeps its label).
        const label = s.key === 'dscr' ? 'DSCR loan' : s.label;
        // Leading space: the tag follows the label inline.
        const basisTag = s.key === activeKey ? ' <span class="inv-fin-basis">Your basis</span>' : '';
        if (!r) {
          // Unloaded / failed row: one placeholder spanning the four
          // numeric columns (.inv-fin-pending → grid-column: 2 / -1).
          return `<div class="${rowCls}"><div><div class="inv-fin-label">${escapeHtml(label)}${basisTag}</div></div><div class="inv-fin-pending">\u2014</div></div>`;
        }
        const dp = Number((r.mortgage as AnyRecord)?.down_payment || 0);
        const pi = Number((r.mortgage as AnyRecord)?.monthly_payment || 0);
        const cf = Number((r.cash_flow as AnyRecord)?.monthly_pre_tax || 0);
        // This row's own DSCR (NOI ÷ this row's debt service). Cash has
        // no debt, so no ratio — shared NA, same as every other surface.
        const rowDscr = s.key === 'cash' ? null : toNumber(r.dscr_ratio);
        return `
          <div class="${rowCls}"${s.key === activeKey ? ' aria-current="true"' : ''}>
            <div>
              <div class="inv-fin-label">${escapeHtml(label)}${basisTag}</div>
              <div class="inv-fin-blurb">${escapeHtml(s.blurb + terms)}</div>
            </div>
            <div>${fmtMoney(dp)}</div>
            <div>${s.key === 'cash' ? '—' : fmtMoney(pi)}</div>
            <div class="${cf >= 0 ? 'pos' : 'neg'}"><strong>${fmtMoney(cf)}</strong></div>
            <div${rowDscr == null ? ' class="inv-tile-na"' : ` style="color:${dscrColor(rowDscr)}"`}>${rowDscr == null ? NA : rowDscr.toFixed(2)}</div>
          </div>
        `;
      }).join('')}
    </div>
  `;
}

// ─── Rent Estimate ───────────────────────────────────────────────────

function renderRentComps(listingId: string, data: AnyRecord): void {
  const container = document.getElementById('inv-rent-comps-content');
  if (!container) return;
  const est = data.rent_estimate as AnyRecord | undefined;
  const rent = Number(data.estimated_monthly_rent || 0);
  const confidence = String(est?.confidence || 'medium');
  const method = String(est?.methodology || 'market_ratio');
  const rangeLow = Number(est?.rent_range_low || 0);
  const rangeHigh = Number(est?.rent_range_high || 0);

  const confLabel =
    confidence === 'high' ? 'High confidence' : confidence === 'medium' ? 'Medium confidence' : 'Rough estimate';
  const methodLabel =
    method === 'user_override'
      ? 'From your override'
      : method === 'market_ratio'
        ? 'Estimate from metro rent-to-price ratio'
        : method === 'stored_estimate'
          ? 'From stored estimate'
          : 'Estimate';

  container.innerHTML = `
    <div class="inv-rent-hero">
      <div class="inv-rent-value">${fmtMoney(rent)}<span class="inv-rent-unit">/mo</span></div>
      <div class="inv-rent-confidence inv-rent-conf-${confidence}">${confLabel}</div>
    </div>
    <div class="inv-rent-methodology">${methodLabel}${rangeLow > 0 && rangeHigh > 0 ? ` · Typical range ${fmtMoney(rangeLow)}&ndash;${fmtMoney(rangeHigh)}` : ''}</div>
    <div class="inv-rent-methodology" style="opacity:.7">Live rental comps aren't connected yet — verify rent against Rentometer or Zillow before making an offer.</div>
    <details class="inv-deal-breakdown inv-collapse inv-rent-adjust">
      <summary>Adjust rent</summary>
      <div class="inv-rent-override">
        <label for="inv-rent-override-input-${escapeHtml(listingId)}">Override rent</label>
        <input type="number" id="inv-rent-override-input-${escapeHtml(listingId)}" placeholder="$ /mo" min="0">
        <button class="btn btn-secondary btn-sm" onclick="applyInvRentOverride('${escapeHtml(listingId)}')">Apply</button>
      </div>
    </details>
  `;
}

export function applyInvRentOverride(listingId: string): void {
  const input = document.getElementById(`inv-rent-override-input-${listingId}`) as HTMLInputElement | null;
  if (!input) return;
  const v = Number(input.value);
  if (!v || v <= 0) {
    showToast('Enter a positive rent', 'error');
    return;
  }
  const a = getAssumptions(listingId);
  a.estimated_rent_override = v;
  loadInvestorDetail(listingId);
}

// ─── Offer Strategy (strategy-aware AI offer guidance) ──────────────────
//
// Backs into a target offer price from the investor's strategy +
// criteria + comp data. See services/offer_strategy.py for the formulas.

async function loadOfferStrategy(listingId: string, strategyOverride?: string): Promise<void> {
  const el = document.getElementById('inv-offer-strategy-content');
  if (!el) return;
  // Wishlist #7: thread the optional strategy override into the API call.
  // Lets the user pivot between Buy & Hold / BRRRR / Fix & Flip / STR /
  // Wholesale on the detail page without modifying their saved profile.
  const params = new URLSearchParams();
  if (strategyOverride) params.set('strategy', strategyOverride);
  // The same rent override + vacancy the Deal Analysis section was just
  // computed with (the /investment-analysis/custom body), so "cap at this
  // price" at list is the cap rate shown up the page, not a second number.
  const a = getAssumptions(listingId);
  params.set('vacancy_rate', String(a.vacancy_rate));
  if (a.estimated_rent_override != null && a.estimated_rent_override > 0) {
    params.set('estimated_rent_override', String(a.estimated_rent_override));
  }
  try {
    const data = await api<AnyRecord>(
      'GET',
      `/listings/${encodeURIComponent(listingId)}/offer-strategy?${params.toString()}`,
    );
    renderOfferStrategy(el, data, listingId);
  } catch (_err) {
    el.innerHTML = `
      <div style="color:var(--text-secondary);font-size:13px;padding:4px 0">
        Strategy not available for this listing yet.
      </div>
    `;
  }
}

/** Wishlist #7 inline handler — re-fetches the Offer Strategy block
 *  with a different strategy without touching the user's saved profile. */
export function switchOfferStrategy(listingId: string, strategy: string): void {
  void loadOfferStrategy(listingId, strategy);
}

/**
 * "$X/mo gross at full occupancy (2.5× LTR $Y) · 65% occupancy · 20% mgmt ·
 * 8% turnover reserve" from Offer Strategy's `str_assumptions`. Returns ''
 * when the block is absent (every non-STR strategy) or has no revenue.
 */
function strUnderwritingLine(a: StrAssumptions | null): string {
  if (!a) return '';
  const gross = toNumber(a.gross_monthly_revenue);
  if (gross == null || gross <= 0) return '';
  const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
  const mult = toNumber(a.gross_multiplier);
  const ltr = toNumber(a.long_term_rent);
  const vacancy = toNumber(a.vacancy_pct);
  const mgmt = toNumber(a.mgmt_pct);
  const reserve = toNumber(a.maintenance_pct);
  const parts: string[] = [];
  const basis = mult != null && ltr != null ? ` (${mult.toFixed(1)}\u00d7 LTR ${money(ltr)})` : '';
  parts.push(`${money(gross)}/mo gross at full occupancy${basis}`);
  if (vacancy != null) parts.push(`${Math.round(100 - vacancy)}% occupancy`);
  if (mgmt != null) parts.push(`${Math.round(mgmt)}% mgmt`);
  if (reserve != null) parts.push(`${Math.round(reserve)}% turnover reserve`);
  return parts.join(' \u00b7 ');
}

function renderOfferStrategy(el: HTMLElement, data: AnyRecord, listingId: string): void {
  const listPrice = Number(data.list_price || 0);
  const target = Number(data.target_offer || 0);
  const band = (data.offer_band as AnyRecord) || {};
  const tactics = (data.negotiation_tactics as AnyRecord[]) || [];
  const win = Number(data.win_probability || 0);
  const temperature = String(data.market_temperature || 'balanced');
  const strategyLabel = String(data.strategy_label || 'Buy & Hold');
  const activeStrategy = String(data.strategy || 'buy_and_hold');
  const rationale = String(data.rationale || '');
  const targetCap = data.target_cap_rate != null ? Number(data.target_cap_rate) : null;
  // The cap rate the deal actually earns at the target offer. It can
  // differ from the "Target X% cap" chip — when the 50%-of-list floor or
  // "met at list" applies, the rationale points here ("the cap there is
  // shown below") — so it is shown next to the offer, tinted by the
  // shared capRateLevel bands; shared NA when the payload has none.
  const capAtOffer = toNumber(data.cap_rate_at_offer);
  const capAtOfferText = capAtOffer == null ? NA : `${capAtOffer.toFixed(2)}%`;
  // Non-null only for short_term_rental: the assumptions the STR NOI was
  // solved with. Every number comes from the payload — nothing hardcoded.
  const strLine = strUnderwritingLine((data.str_assumptions as StrAssumptions | null | undefined) ?? null);

  // Wishlist #7: strategy chip selector. Lets the investor pivot between
  // strategies on this single listing without changing their saved
  // profile. Clicking a chip re-fetches the strategy block with the new
  // strategy as a query param.
  const strategyOptions: { value: string; label: string }[] = [
    { value: 'buy_and_hold', label: 'Buy & Hold' },
    { value: 'brrrr', label: 'BRRRR' },
    { value: 'flip', label: 'Fix & Flip' },
    { value: 'short_term_rental', label: 'STR' },
    { value: 'wholesale', label: 'Wholesale' },
  ];
  const strategyChipRow = `
    <div style="display:flex;flex-wrap:wrap;gap:6px;margin:0 0 12px">
      ${strategyOptions.map((s) => {
        const active = s.value === activeStrategy;
        return `
          <button
            type="button"
            onclick="switchOfferStrategy('${escapeHtml(listingId)}', '${s.value}')"
            style="padding:6px 12px;border-radius:14px;font-size:12px;font-weight:700;cursor:pointer;border:1px solid ${active ? 'var(--primary)' : 'var(--border)'};background:${active ? 'var(--primary)' : 'transparent'};color:${active ? '#fff' : 'var(--text-secondary)'}"
          >${escapeHtml(s.label)}</button>
        `;
      }).join('')}
    </div>
  `;

  // How far the target offer sits below list — tells investor at a glance
  // how aggressive the recommendation is.
  const discount = listPrice > 0 && target > 0 ? ((listPrice - target) / listPrice) * 100 : 0;

  const tempColor =
    temperature === 'cold'
      ? 'inv-verdict-green'
      : temperature === 'hot'
        ? 'inv-verdict-red'
        : 'inv-verdict-yellow';

  const winColor =
    win >= 70 ? 'var(--success)' : win >= 40 ? 'var(--warning)' : 'var(--danger)';

  const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;

  el.innerHTML = `
    ${strategyChipRow}

    <div class="inv-strategy-head">
      <div class="inv-strategy-chip">${escapeHtml(strategyLabel)}</div>
      ${targetCap ? `<div class="inv-strategy-chip-muted">Target ${targetCap.toFixed(1)}% cap</div>` : ''}
      <div class="inv-strategy-chip ${tempColor}">${temperature.toUpperCase()}</div>
    </div>

    <div class="inv-strategy-hero">
      <div class="inv-strategy-price">${fmt(target)}</div>
      <div class="inv-strategy-discount">
        ${discount > 0 ? `${discount.toFixed(1)}% below list` : discount < 0 ? `${Math.abs(discount).toFixed(1)}% above list` : 'at list'}
      </div>
      <div class="inv-strategy-cap-at" role="group" aria-label="Cap at this price: ${escapeHtml(capAtOfferText)}">
        Cap at this price: <strong${capAtOffer == null ? ' class="inv-tile-na"' : ` style="color:${capColor(capAtOffer)}"`}>${escapeHtml(capAtOfferText)}</strong>
      </div>
    </div>

    <div class="inv-strategy-band">
      <div class="inv-strategy-band-col">
        <div class="inv-strategy-band-label">Strong</div>
        <div class="inv-strategy-band-value">${fmt(Number(band.strong || 0))}</div>
      </div>
      <div class="inv-strategy-band-col inv-strategy-band-target">
        <div class="inv-strategy-band-label">Target</div>
        <div class="inv-strategy-band-value">${fmt(Number(band.target || 0))}</div>
      </div>
      <div class="inv-strategy-band-col">
        <div class="inv-strategy-band-label">Stretch</div>
        <div class="inv-strategy-band-value">${fmt(Number(band.stretch || 0))}</div>
      </div>
    </div>

    <div class="inv-strategy-win">
      <div class="inv-strategy-win-label">Win probability</div>
      <div class="inv-strategy-win-bar">
        <div class="inv-strategy-win-fill" style="width:${win}%;background:${winColor}"></div>
      </div>
      <div class="inv-strategy-win-value" style="color:${winColor}">${win}%</div>
    </div>

    ${strLine ? `<div class="inv-strategy-str" role="group" aria-label="STR underwriting: ${escapeHtml(strLine)}"><span class="inv-strategy-str-label">STR underwriting</span> ${escapeHtml(strLine)}</div>` : ''}

    ${rationale ? `<div class="inv-strategy-rationale">${escapeHtml(rationale)}</div>` : ''}

    ${tactics.length > 0 ? `
      <details class="inv-deal-breakdown inv-collapse">
      <summary>Negotiation playbook (${tactics.length})</summary>
      <div class="inv-strategy-tactics">
        ${tactics.map((t) => `
          <div class="inv-strategy-tactic">
            <div class="inv-strategy-tactic-title">
              <i data-feather="check" width="14"></i>
              <span>${escapeHtml(String(t.title || ''))}</span>
            </div>
            <div class="inv-strategy-tactic-detail">${escapeHtml(String(t.detail || ''))}</div>
          </div>
        `).join('')}
      </div>
      </details>
    ` : ''}
  `;

  if (window.feather) window.feather.replace();
}
